/* eslint-disable sort-keys */
import { Portal } from '@components/Datagrid/components/Portal'
import type { Theme as GlideTheme } from '@glideapps/glide-data-grid'
import { DataEditor } from '@glideapps/glide-data-grid'
import { alpha, CardContent, decomposeColor, recomposeColor, styled, useTheme } from '@mui/material'
import type { ComponentProps } from 'react'
import { useMemo } from 'react'

export const cellHeight = 40
// Body rows breathe a touch more than the 40px header
export const dataRowHeight = 44

/**
 * glide-data-grid paints `bgCell` opaquely, so a translucent wash has to be
 * pre-blended against the canvas colour instead of composited by the browser.
 * Keeps datagrid tints derived from palette tokens rather than baked literals.
 */
export const blendOpaque = (foreground: string, background: string, opacity: number) => {
  const [fgRed, fgGreen, fgBlue] = decomposeColor(foreground).values
  const [bgRed, bgGreen, bgBlue] = decomposeColor(background).values
  const mix = (fg: number, bg: number) => Math.round(fg * opacity + bg * (1 - opacity))

  return recomposeColor({
    type: 'rgb',
    values: [mix(fgRed, bgRed), mix(fgGreen, bgGreen), mix(fgBlue, bgBlue)],
  })
}

const rightColumnBoxShadow = '-8px 0px 8px -4px rgba(0, 0, 0, 0.12)'

interface ActionBtnBarProps {
  actionButtonPosition?: 'left' | 'right'
}

export const ActionBtnBar = styled('div')<ActionBtnBarProps>(
  ({ theme, actionButtonPosition = 'left' }) => ({
    position: 'absolute',
    left: actionButtonPosition === 'left' ? 0 : 'auto',
    right: actionButtonPosition === 'right' ? 0 : 'auto',
    zIndex: 1,
    background: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    width: `calc(100% - 64px - ${cellHeight}px - 1px)`,
    marginTop: 1,
    marginLeft: 50,
    height: cellHeight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0.75),
  }),
)

export const ColumnPicker = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  height: cellHeight,
  width: '100%',
})

export const ColumnPickerBackground = styled(ColumnPicker)(({ theme }) => ({
  background: theme.palette.background.default,
}))

export const DatagridPortal = styled(Portal)(({ theme }) => ({
  '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
    appearance: 'none',
    margin: 0,
  },
  '& input[type=number]': {
    appearance: 'textfield',
  },
  '& .clip-region': {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  '& .gdg-growing-entry': {
    flex: 1,
    marginTop: 0,
  },
  '& .gdg-style': {
    background: theme.palette.background.default,
    border: 'none',
    boxShadow: 'none !important',
    padding: '0 !important',
  },
  "& input:not([class*='MuiInputBase']), & textarea": {
    appearance: 'none',
    background: 'none',
    border: 'none',
    fontSize: theme.typography.body2.fontSize,
    padding: theme.spacing(0.5),
    outline: 0,
  },
  "& input[type='number']:not([class*='MuiInputBase'])": {
    textAlign: 'right',
    width: '100%',
  },
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: 2,
}))

interface RowActionBarProps {
  width?: number
  isScrolledToRight?: boolean
}

export const RowActionBar = styled('div', {
  shouldForwardProp: (propName: PropertyKey) =>
    propName !== 'width' && propName !== 'isScrolledToRight',
})<RowActionBarProps>(({ theme, width = 36, isScrolledToRight }) => ({
  height: '100%',
  width,
  ...(isScrolledToRight
    ? {
        borderLeftColor: theme.palette.divider,
      }
    : {}),
}))

interface RowColumnGroupProps {
  isScrolledToRight?: boolean
}

export const RowColumnGroup = styled('div')<RowColumnGroupProps>(
  ({ theme, isScrolledToRight }) => ({
    '&:hover': {
      background: 'transparent',
      color: theme.palette.divider,
    },
    '&:not(:last-child)': {
      marginBottom: -1,
    },
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRight: 'none',
    color: theme.palette.text.primary,
    display: 'grid',
    gridTemplateColumns: '1fr',
    background: theme.palette.background.default,
    boxShadow: rightColumnBoxShadow,
    height: dataRowHeight,
    marginLeft: '-4px',
    ...(isScrolledToRight && {
      borderLeftColor: theme.palette.divider,
    }),
  }),
)

export const ColumnGroupFixer = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 1,
  left: 0,
  height: cellHeight,
  width: 10,
  borderLeft: 0,
  background: theme.palette.background.default,
}))

export const EditorContainer = styled('div')({
  position: 'relative',
  height: '100%',
})

interface RowActionBarShadowProps {
  isActive?: boolean
}

export const RowActionBarShadow = styled('div')<RowActionBarShadowProps>(({ isActive }) => ({
  height: '100%',
  width: 1,
  position: 'absolute',
  zIndex: -1,
  transition: 'box-shadow .2s ease-in-out',
  // Active = more columns hidden to the right; the shadow is the affordance.
  boxShadow: isActive ? '-4px 0px 12px rgba(0, 0, 0, 0.2)' : '-1px 0px 12px transparent',
}))

export const DatagridEditor = styled(
  ({
    datagridTheme,
    ...props
  }: Omit<ComponentProps<typeof DataEditor>, 'theme'> & {
    datagridTheme: Partial<GlideTheme>
  }) => <DataEditor {...props} theme={datagridTheme} />,
)(({ theme }) => ({
  '#modal-root &': {
    height: '100%',
    '& .dvn-scroller': {
      overflowY: 'scroll',
    },
  },
  '& .dvn-scroller': {
    overscrollBehaviorX: 'none',
    overflowY: 'hidden',
    // Custom scrollbar styling for webkit browsers
    '&::-webkit-scrollbar': {
      height: '8px',
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.action.disabled,
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.action.selected,
      },
    },
    // Firefox scrollbar styling
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.action.disabled} transparent`,
  },
  borderRadius: 0,
  boxSizing: 'content-box',
  width: '100%',
  paddingBottom: '1px',
}))

export const DatagridCard = styled('div', {
  shouldForwardProp: (propName: PropertyKey) => propName !== 'isOpen',
})<{ isOpen: boolean }>(({ isOpen }) => ({
  position: 'relative',
  height: isOpen ? '100%' : 'auto',
  gap: 0,
  display: 'flex',
  flexDirection: 'column',
}))

export const DatagridCardContent = styled(CardContent)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: 0,
  paddingRight: 0,
  paddingTop: 0,
  paddingBottom: 0,
  // One card gutter for the whole list: the canvas lines up with the toolbar
  // above it instead of bleeding past both card edges. Below `sm` the mobile
  // card list brings its own padding.
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  '&:last-child': {
    paddingBottom: 0,
  },
}))

export const DatagridTopBorder = styled('div')<{ showTopBorder: boolean }>(
  ({ theme, showTopBorder }) => ({
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.divider,
    borderTopStyle: 'solid',
    borderTopWidth: showTopBorder ? 1 : 0,
    // The canvas is inset by the card gutter; the rule under the toolbar is
    // not — it separates the two full card-width bands.
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(-3),
      marginRight: theme.spacing(-3),
    },
  }),
)

export const DatagridRowAnchor = styled('a')({
  left: '-1000px',
  position: 'absolute',
  top: '-1000px',
})

export const LoaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(4.5),
  marginTop: theme.spacing(4.5),
}))

// EmptyState brings its own padding and centring, so this only has to give it
// the full width under the (still rendered) column header row.
export const EmptyContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
})

export function useDatagridTheme() {
  const theme = useTheme()

  return useMemo(
    (): Partial<GlideTheme> => ({
      accentColor: 'transparent',
      // Selected rows tint with a teal wash
      accentLight: alpha(theme.palette.primary.main, 0.12),
      accentFg: 'transparent',
      bgCell: theme.palette.background.default,
      bgHeader: theme.palette.background.default,
      bgHeaderHasFocus: 'transparent',
      bgHeaderHovered: 'transparent',
      bgBubbleSelected: theme.palette.background.default,
      borderColor: theme.palette.divider,
      fontFamily: "'Inter var', sans-serif",
      baseFontStyle: `${theme.typography.fontWeightRegular} ${theme.typography.body1.fontSize}`,
      headerFontStyle: `${theme.typography.fontWeightMedium} ${theme.typography.body2.fontSize}`,
      editorFontSize: `${theme.typography.body1.fontSize}`,
      textMedium: theme.palette.text.primary,
      textGroupHeader: theme.palette.text.primary,
      textBubble: theme.palette.background.default,
      textDark: theme.palette.text.primary,
      textLight: theme.palette.text.secondary,
      textHeader: theme.palette.text.primary,
      textHeaderSelected: theme.palette.text.primary,
      fgIconHeader: theme.palette.text.primary,
      cellHorizontalPadding: 8,
      cellVerticalPadding: 8,
      lineHeight: 20,
    }),
    [theme],
  )
}

export { rightColumnBoxShadow }
